import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ImagePlus, Trash2, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import { Reorder, useDragControls } from 'framer-motion';
import { useVenue } from '../../contexts/VenueContext';
import { API, fetchSelectedVenueApplication, getAuthHeaders, saveSelectedVenueApplication } from '../../utils/venueAppApi';
import { RouteTransitionLogo } from '../../components/RouteTransitionLogo';
import { IntroVideoUploader } from '../../components/venue/IntroVideoUploader';
import { VenueSetupBackButton } from '../../components/venue/VenueSetupBackButton';
import { SettingStatusPill } from '../../components/venue/SettingStatusPill';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_PHOTOS = 25;
const font = "'Inria Serif', serif";
const cardStyle = {
  backgroundColor: 'var(--app-bg)',
  borderRadius: 20,
  border: '1px solid var(--card-border)',
  boxShadow: 'var(--premium-shadow-main)',
};

function PhotoRow({ url, idx, onDelete, onOrderPersist }) {
  const controls = useDragControls();
  return (
    <Reorder.Item
      value={url}
      dragListener={false}
      dragControls={controls}
      data-testid={`venue-photo-item-${idx}`}
      onDragEnd={onOrderPersist}
      whileDrag={{ scale: 1.02, zIndex: 5, boxShadow: '0 18px 32px rgba(0, 0, 0, 0.40)' }}
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: 8,
        borderRadius: 14,
        overflow: 'hidden',
        background: 'var(--surface)',
        border: '1px solid var(--card-border)',
        listStyle: 'none',
      }}
    >
      {/* Drag handle — the ONLY element that starts a reorder drag.
          The rest of the row remains scrollable/tappable. */}
      <span
        aria-label="Drag to reorder"
        data-testid={`venue-photo-drag-handle-${idx}`}
        onPointerDown={(e) => controls.start(e)}
        style={{
          width: 28,
          height: 28,
          borderRadius: 6,
          background: 'var(--accent-surface-soft)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--accent-purple)',
          flexShrink: 0,
          cursor: 'grab',
          touchAction: 'none',
          userSelect: 'none',
          WebkitUserSelect: 'none',
        }}
      >
        <GripVertical size={16} strokeWidth={2.2} />
      </span>
      <div
        style={{
          position: 'relative',
          width: 60,
          height: 60,
          borderRadius: 10,
          overflow: 'hidden',
          flexShrink: 0,
          background: 'var(--accent-surface)',
        }}
      >
        <img
          src={url.startsWith('/') ? `${BACKEND_URL}${url}` : url}
          alt={`Photo ${idx + 1}`}
          draggable={false}
          style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }}
        />
      </div>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontFamily: font, fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>
          {idx === 0 ? 'Cover photo' : `Photo ${idx + 1}`}
        </span>
        <span style={{ fontFamily: font, fontSize: 11.5, color: 'var(--text-3)' }}>
          {idx === 0 ? 'Shown first on your venue card' : 'Tap trash to remove'}
        </span>
      </div>
      {idx === 0 ? (
        <span
          data-testid={`venue-photo-cover-${idx}`}
          style={{
            padding: '4px 9px',
            borderRadius: 999,
            background: 'var(--accent-purple)',
            color: '#FFFFFF',
            fontFamily: font,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            flexShrink: 0,
          }}
        >
          Cover
        </span>
      ) : null}
      {/* Always-visible delete button */}
      <button
        type="button"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); onDelete(idx); }}
        data-testid={`venue-photo-remove-${idx}`}
        aria-label="Remove photo"
        style={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          background: 'rgba(214, 50, 50, 0.10)',
          border: '1px solid rgba(214, 50, 50, 0.20)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
          flexShrink: 0,
          transition: 'background 140ms ease',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(214,50,50,0.22)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(214,50,50,0.10)'; }}
      >
        <Trash2 size={15} color="#B33232" strokeWidth={2} />
      </button>
    </Reorder.Item>
  );
}

export default function VenuePhotosPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeVenueAppId } = useVenue();
  const [photos, setPhotos] = useState([]);
  const [introVideoUrl, setIntroVideoUrl] = useState('');
  const [loading, setLoading] = useState(true);
  // Keep a ref to the latest `photos` so `onDragEnd` (which can have a
  // stale closure) always saves the most recent order.
  const photosRef = useRef([]);
  useEffect(() => { photosRef.current = photos; }, [photos]);

  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        const application = await fetchSelectedVenueApplication(activeVenueAppId);
        setPhotos(application?.photo_urls || []);
        setIntroVideoUrl(application?.intro_video_url || '');
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchPhotos();
  }, [activeVenueAppId]);

  const persistOrder = async (newOrder) => {
    try {
      await saveSelectedVenueApplication(
        { photo_urls: newOrder, step: 7, submitted: false },
        activeVenueAppId,
      );
    } catch (err) {
      toast.error('Failed to save new order');
    }
  };

  const handleReorder = (newOrder) => {
    setPhotos(newOrder);
  };

  const handleDelete = async (idx) => {
    const newPhotos = photos.filter((_, i) => i !== idx);
    setPhotos(newPhotos);
    try {
      await saveSelectedVenueApplication({ photo_urls: newPhotos, step: 7, submitted: false }, activeVenueAppId);
      toast.success('Photo removed');
    } catch (err) { toast.error('Failed to save'); }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const currentCount = photosRef.current.length;
    const remaining = MAX_PHOTOS - currentCount;
    if (remaining <= 0) {
      toast.error(`You can only have up to ${MAX_PHOTOS} photos`);
      e.target.value = '';
      return;
    }
    let filesToUpload = files;
    if (files.length > remaining) {
      filesToUpload = files.slice(0, remaining);
      toast.error(`Only ${remaining} more photo${remaining === 1 ? '' : 's'} allowed (max ${MAX_PHOTOS})`);
    }

    let uploaded = 0;
    const toastId = filesToUpload.length > 1 ? toast.loading(`Uploading photos (0/${filesToUpload.length})…`) : null;

    for (const file of filesToUpload) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await axios.post(`${API}/venue/upload-photo`, formData, {
          headers: { ...getAuthHeaders() }
        });
        uploaded++;
        setPhotos(prev => {
          const newPhotos = [...prev, res.data.url];
          saveSelectedVenueApplication({ photo_urls: newPhotos, step: 7, submitted: false }, activeVenueAppId)
            .catch(() => {});
          return newPhotos;
        });
        if (toastId) toast.loading(`Uploading photos (${uploaded}/${filesToUpload.length})…`, { id: toastId });
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    if (toastId) toast.dismiss(toastId);
    if (uploaded > 0) toast.success(uploaded === 1 ? 'Photo uploaded' : `${uploaded} photos uploaded`);
    e.target.value = '';
  };

  const handleIntroVideoChange = async (url) => {
    setIntroVideoUrl(url);
    try {
      await saveSelectedVenueApplication(
        { intro_video_url: url, step: 7, submitted: false },
        activeVenueAppId,
      );
    } catch (err) {
      console.error('Failed to save intro video:', err);
      toast.error("Couldn't save the intro video. Please try again.");
    }
  };

  return (
    <div className="app-shell" style={{ minHeight: '100vh', backgroundColor: 'var(--app-bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingBottom: 40 }}>
      <div style={{ width: '100%', maxWidth: 390, display: 'flex', justifyContent: 'flex-start', padding: '16px 20px 0' }}>
        <VenueSetupBackButton testId="venue-photos-back" />
      </div>
      <div style={{ marginTop: 8 }}>
        <RouteTransitionLogo width={100} height={89} dataTestId="venue-photos-shared-logo" />
      </div>
      <h2 style={{ fontFamily: font, fontSize: 26, fontWeight: 700, color: 'var(--text)', marginTop: 8 }}>Photos & Video</h2>
      <p style={{ fontFamily: font, fontSize: 15, color: 'var(--text-3)', marginBottom: 12, textAlign: 'center', maxWidth: 320 }}>Drag photos to reorder — the first one is your cover. Add an intro video to loop when guests open your venue.</p>

      {!loading ? (
        <div style={{ marginBottom: 14 }}>
          <SettingStatusPill
            complete={photos.length >= 10}
            missingText={photos.length === 0
              ? `Missing at least 10 photos (max ${MAX_PHOTOS})`
              : `Missing ${10 - photos.length} more photo${10 - photos.length === 1 ? '' : 's'}`}
            testId="venue-photos-status-pill"
          />
        </div>
      ) : null}

      {/* Intro video uploader */}
      <div style={{ width: 'calc(100% - 32px)', maxWidth: 360, marginBottom: 8 }}>
        <IntroVideoUploader
          value={introVideoUrl}
          onChange={handleIntroVideoChange}
          testId="venue-photos-intro-video"
          getAuthHeaders={getAuthHeaders}
        />
      </div>

      {/* Photo list — single column for reliable drag-to-reorder */}
      <div style={{ ...cardStyle, width: 'calc(100% - 32px)', maxWidth: 360, padding: 12 }}>
        <Reorder.Group
          axis="y"
          values={photos}
          onReorder={handleReorder}
          data-testid="venue-photos-grid"
          style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          {photos.map((url, idx) => (
            <PhotoRow
              key={url}
              url={url}
              idx={idx}
              onDelete={handleDelete}
              onOrderPersist={() => persistOrder(photosRef.current)}
            />
          ))}
        </Reorder.Group>
      </div>

      {/* Upload button — full-width accent pill */}
      <label
        data-testid="venue-photos-upload-label"
        aria-disabled={photos.length >= MAX_PHOTOS}
        style={{
          marginTop: 16,
          width: 'calc(100% - 32px)',
          maxWidth: 360,
          cursor: photos.length >= MAX_PHOTOS ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
          padding: '14px 24px',
          borderRadius: 20,
          background: 'var(--accent-purple)',
          boxShadow: 'var(--premium-shadow-main)',
          opacity: photos.length >= MAX_PHOTOS ? 0.45 : 1,
          transition: 'opacity 150ms ease',
        }}
        onMouseEnter={(e) => { if (photos.length < MAX_PHOTOS) e.currentTarget.style.opacity = '0.88'; }}
        onMouseLeave={(e) => { if (photos.length < MAX_PHOTOS) e.currentTarget.style.opacity = '1'; }}
      >
        <ImagePlus size={20} color="#FFFFFF" strokeWidth={2.2} />
        <span style={{ fontFamily: font, fontSize: 18, fontWeight: 700, color: '#FFFFFF' }}>
          {photos.length >= MAX_PHOTOS ? `Max ${MAX_PHOTOS} photos reached` : `Add photos (${photos.length}/${MAX_PHOTOS})`}
        </span>
        <input
          type="file"
          accept="image/*"
          multiple
          onChange={handleUpload}
          disabled={photos.length >= MAX_PHOTOS}
          style={{ display: 'none' }}
        />
      </label>
    </div>
  );
}
